import { useState } from 'react';
import { useUpdateOrder, type OrderUpdateData } from '../hooks/useOrderMutations';
import { Save, X, Loader2 } from 'lucide-react';
import type { SalesOrderStatus } from 'scheduler-types';

interface OrderEditFormProps {
  order: {
    id: string;
    customerName: string;
    promisedShipDate: string;
    status: SalesOrderStatus;
  };
  onClose: () => void;
  onSuccess?: () => void;
}

export function OrderEditForm({ order, onClose, onSuccess }: OrderEditFormProps) {
  const [customerName, setCustomerName] = useState(order.customerName);
  const [promisedShipDate, setPromisedShipDate] = useState(order.promisedShipDate);
  const [status, setStatus] = useState<SalesOrderStatus>(order.status);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateOrder(order.id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const updates: OrderUpdateData = {
      customerName,
      promisedShipDate,
      status,
    };

    updateMutation.mutate(updates, {
      onSuccess: () => {
        onSuccess?.();
        onClose();
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Failed to update order');
      },
    });
  };

  const hasChanges =
    customerName !== order.customerName ||
    promisedShipDate !== order.promisedShipDate ||
    status !== order.status;

  return (
    <form onSubmit={handleSubmit} className="bg-background-subtle rounded-lg p-4 mt-4 border border-border">
      <h3 className="text-sm font-medium text-foreground mb-4">Edit Order</h3>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-danger-subtle border border-danger text-sm text-danger-foreground">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label
            htmlFor="customerName"
            className="block text-sm font-medium text-foreground-muted mb-1"
          >
            Customer Name
          </label>
          <input
            id="customerName"
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          />
        </div>

        <div>
          <label
            htmlFor="promisedShipDate"
            className="block text-sm font-medium text-foreground-muted mb-1"
          >
            Promised Ship Date
          </label>
          <input
            id="promisedShipDate"
            type="date"
            value={promisedShipDate}
            onChange={(e) => setPromisedShipDate(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          />
        </div>

        <div>
          <label
            htmlFor="status"
            className="block text-sm font-medium text-foreground-muted mb-1"
          >
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as SalesOrderStatus)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="complete">Complete</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border">
        <button
          type="button"
          onClick={onClose}
          disabled={updateMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-muted transition-colors disabled:opacity-50"
        >
          <X className="h-4 w-4" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={updateMutation.isPending || !hasChanges}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </form>
  );
}
